import React from "react";

interface LiquidGlassProps {
  children: React.ReactNode;
  width?: number;
  height?: number;
  borderRadius?: number;
  innerShadowColor?: string;
  innerShadowBlur?: number;
  innerShadowSpread?: number;
  glassTintColor?: string;
  glassTintOpacity?: number;
  frostBlurRadius?: number;
  noiseFrequency?: number;
  noiseStrength?: number;
  className?: string;
}

const LiquidGlass: React.FC<LiquidGlassProps> = ({
  children,
  width,
  height,
  borderRadius = 28,
  innerShadowColor = "#000000",
  innerShadowBlur = 16,
  innerShadowSpread = -4,
  glassTintColor = "rgba(255, 255, 255, 0.06)",
  glassTintOpacity = 6,
  frostBlurRadius = 2,
  noiseFrequency = 0.006,
  noiseStrength = 19,
  className = "",
}) => {
  const filterId = `noise-${Math.random().toString(36).substr(2, 9)}`;

  // Asegurar que el blur nunca sea negativo
  const safeBlurRadius = Math.max(0, frostBlurRadius);

  return (
    <div
      className={`liquid-glass-container ${className}`}
      style={{
        width: width ? `${width}px` : "100%",
        height: height ? `${height}px` : "auto",
        minHeight: "100%",
        borderRadius: `${borderRadius}px`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* SVG Filter for noise */}
      <svg
        style={{ position: "absolute", width: 0, height: 0 }}
        aria-hidden="true"
      >
        <defs>
          <filter id={filterId}>
            <feTurbulence
              type="fractalNoise"
              baseFrequency={noiseFrequency}
              numOctaves="4"
              result="noise"
            />
            <feColorMatrix
              in="noise"
              type="matrix"
              values={`0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 ${
                noiseStrength / 100
              } 0`}
              result="noise"
            />
            <feBlend in="SourceGraphic" in2="noise" mode="multiply" />
          </filter>
        </defs>
      </svg>

      {/* Background layer with frost effect */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: glassTintColor,
          opacity: glassTintOpacity / 100,
          backdropFilter: `blur(${safeBlurRadius}px)`,
          WebkitBackdropFilter: `blur(${safeBlurRadius}px)`,
          borderRadius: `${borderRadius}px`,
          filter: `url(#${filterId})`,
        }}
      />

      {/* Inner shadow effect */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: `${borderRadius}px`,
          boxShadow: `inset 0 0 ${innerShadowBlur}px ${innerShadowSpread}px ${innerShadowColor}`,
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "100%",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default LiquidGlass;
